import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronDown, Folder, Loader2, CheckCircle2, Plus } from 'lucide-react';
import { useInstance } from '../hooks/useInstance';
import { apiClient } from '../services/api';
import { buildTree } from '../utils/treeUtils';
import { CategoryItem, TreeItem } from '../types';

export const ShareTargetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedInstanceId } = useInstance();

  const rawText = (searchParams.get('text') || searchParams.get('title') || '').trim();
  const [word, setWord] = useState(rawText);

  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedInstanceId) return;
    const load = async () => {
      try {
        const data = await apiClient.getCategoriesWithCards(selectedInstanceId);
        const items: CategoryItem[] = data
          .filter((d) => d.category)
          .map((d) => ({
            id: d.category!.id,
            name: d.category!.name,
            isExpanded: false,
            children: buildTree(d.cards || []).filter((t) => t.is_folder),
          }));
        setCategories(items);
      } catch {
        setError('Could not load categories.');
      } finally {
        setIsLoadingCategories(false);
      }
    };
    load();
  }, [selectedInstanceId]);

  const toggleCategory = (id: number) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectCategory = (id: number) => {
    setSelectedCategoryId(id);
    setSelectedFolderId(null);
    setError(null);
  };

  const selectFolder = (folderId: number) => {
    setSelectedFolderId(folderId);
    setSelectedCategoryId(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!word.trim()) { setError('Please enter a word.'); return; }
    if (!selectedCategoryId && !selectedFolderId) { setError('Please select a destination.'); return; }
    setIsSaving(true);
    setError(null);
    try {
      await apiClient.createCard({
        name: word.trim(),
        ...(selectedFolderId ? { parent_id: selectedFolderId } : { category_id: selectedCategoryId! }),
      });
      setSaved(true);
    } catch {
      setError('Could not save the card. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAnother = () => {
    setWord('');
    setSaved(false);
    setSelectedCategoryId(null);
    setSelectedFolderId(null);
  };

  const selectedLabel = (() => {
    if (selectedFolderId) {
      for (const cat of categories) {
        const folder = cat.children.find((f: TreeItem) => f.id === selectedFolderId);
        if (folder) return `${cat.name} › ${folder.name}`;
      }
    }
    if (selectedCategoryId) {
      return categories.find((c) => c.id === selectedCategoryId)?.name ?? null;
    }
    return null;
  })();

  if (saved) {
    return (
      <div className="max-w-md mx-auto flex flex-col items-center justify-center py-16 px-6 text-center gap-6">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        </div>
        <div>
          <p className="text-lg font-semibold text-gray-900">Card added</p>
          <p className="text-sm text-gray-500 mt-1">
            <span className="font-medium text-gray-700">"{word}"</span> was saved to{' '}
            <span className="font-medium text-gray-700">{selectedLabel}</span>.
          </p>
        </div>
        <div className="flex flex-col gap-3 w-full">
          <button
            onClick={() => navigate('/tree')}
            className="w-full px-4 py-3 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors"
          >
            View in vocabulary
          </button>
          <button
            onClick={handleAddAnother}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Add another word
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto space-y-5 pb-10">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Add card</h1>
        <p className="text-sm text-gray-500 mt-0.5">Choose where to save this word.</p>
      </div>

      {/* Word input */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Word or phrase</label>
        <input
          type="text"
          value={word}
          onChange={(e) => setWord(e.target.value)}
          placeholder="Enter a word…"
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/70 focus:border-indigo-300 bg-white text-sm font-medium"
          autoFocus={!word}
        />
      </div>

      {/* Destination picker */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Destination</label>
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden divide-y divide-gray-100">
          {isLoadingCategories && (
            <div className="flex items-center justify-center py-8 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          )}

          {!isLoadingCategories && categories.length === 0 && (
            <p className="py-6 text-center text-sm text-gray-500">No categories found.</p>
          )}

          {categories.map((cat) => {
            const isExpanded = expandedCategories.has(cat.id);
            const isCatSelected = selectedCategoryId === cat.id && !selectedFolderId;
            const hasFolders = cat.children.length > 0;

            return (
              <div key={cat.id}>
                {/* Category row */}
                <div className="flex items-center">
                  <button
                    onClick={() => selectCategory(cat.id)}
                    className={`flex-1 flex items-center gap-3 px-4 py-3.5 text-sm text-left transition-colors ${
                      isCatSelected ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-800 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 border-2 transition-colors ${
                      isCatSelected ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'
                    }`} />
                    {cat.name}
                  </button>
                  {hasFolders && (
                    <button
                      onClick={() => toggleCategory(cat.id)}
                      className="px-3 py-3.5 text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  )}
                </div>

                {/* Folders */}
                {isExpanded && hasFolders && (
                  <div className="bg-gray-50 border-t border-gray-100 divide-y divide-gray-100">
                    {cat.children.map((folder: TreeItem) => {
                      const isFolderSelected = selectedFolderId === folder.id;
                      return (
                        <button
                          key={folder.id}
                          onClick={() => selectFolder(folder.id)}
                          className={`w-full flex items-center gap-3 pl-8 pr-4 py-3 text-sm text-left transition-colors ${
                            isFolderSelected ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 border-2 transition-colors ${
                            isFolderSelected ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'
                          }`} />
                          <Folder className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                          {folder.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <button
        onClick={handleSave}
        disabled={isSaving || !word.trim() || (!selectedCategoryId && !selectedFolderId)}
        className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        {isSaving ? 'Saving…' : 'Add card'}
      </button>
    </div>
  );
};
